import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import ScrollToTop from './components/ScrollToTop';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Dashboard App
import DashboardApp from './pages/Dashboard/DashboardApp';
import GetAccessPage from './pages/GetAccess/GetAccessPage';
import WelcomePage from './pages/Welcome/WelcomePage';
import TransferPage from './pages/Transfer/TransferPage';
import SignInV2 from './pages/SignInV2';

// Marketing site
import SiteLayout from './pages/layouts/SiteLayout';
import HomeV3 from './pages/HomeV3';
import HappensToOtherPeople from './pages/HappensToOtherPeople';
import NeurodivergentExplained from './pages/NeurodivergentExplained';
import PredatorProfile from './pages/PredatorProfile';
import SanTzu from './pages/SanTzu';
import EmotionalManipulation from './pages/EmotionalManipulation';
import AbusePlaybook from './pages/AbusePlaybook';
import SeniorChallenge from './pages/SeniorChallenge';
import FamilyImpact from './pages/FamilyImpact';
import CashCow from './pages/CashCow';
import BadDream from './pages/BadDream';
import DataPrivacy from './pages/DataPrivacy';
import ResourcesPage from './pages/ResourcesPage';
import Leadership from './pages/Leadership';
import Investors from './pages/Investors';
import Careers from './pages/Careers';
import CareersOpenRoles from './pages/CareersOpenRoles';
import Philanthropy from './pages/Philanthropy';
import PhilanthropyGrants from './pages/PhilanthropyGrants';
import LegalHub from './pages/LegalHub';
import NotFound from './pages/NotFound';

function App() {
  // Import test utils for development
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      import('./utils/cookieTestUtils');
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <SubscriptionProvider>
        <div className="App">
          <Routes>
            {/* Get Access Page - Flow 1: New User → Subscribe → Get Access */}
            {/* NOT wrapped in ProtectedRoute - allows unauthenticated users to view plans */}
            <Route path="/get-access" element={<GetAccessPage />} />

            {/* Welcome Page - Post-payment landing with DTA download link */}
            {/* NOT wrapped in ProtectedRoute - handles its own auth + subscription gating */}
            <Route path="/welcome" element={<WelcomePage />} />

            {/* DTA Session Transfer - Flow 3b: DTA → Web seamless handoff */}
            {/* NOT wrapped in ProtectedRoute - handles its own auth via transfer token */}
            <Route path="/transfer" element={<TransferPage />} />

            {/* Sign-in stays reachable outside the marketing chrome */}
            <Route path="/signin" element={<SignInV2 />} />

            {/* Dashboard Routes - Separate authentication and functionality */}
            <Route path="/dashboard/*" element={<DashboardApp />} />

            {/* Marketing home */}
            <Route path="/" element={<SiteLayout />}>
              <Route index element={<HomeV3 />} />
            </Route>

            {/* Content sub-pages — these carry their own header/footer chrome */}
            <Route path="/happens-to-other-people" element={<HappensToOtherPeople />} />
            <Route path="/neurodivergent-explained" element={<NeurodivergentExplained />} />
            <Route path="/predator-profile" element={<PredatorProfile />} />
            <Route path="/san-tzu" element={<SanTzu />} />
            <Route path="/emotional-manipulation" element={<EmotionalManipulation />} />
            <Route path="/abuse-playbook" element={<AbusePlaybook />} />
            <Route path="/senior-challenge" element={<SeniorChallenge />} />
            <Route path="/family-impact" element={<FamilyImpact />} />
            <Route path="/cash-cow" element={<CashCow />} />
            <Route path="/bad-dream" element={<BadDream />} />

            {/* Company pages */}
            <Route path="/data-privacy" element={<DataPrivacy />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/leadership" element={<Leadership />} />
            <Route path="/investors" element={<Investors />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/open-roles" element={<CareersOpenRoles />} />
            <Route path="/philanthropy" element={<Philanthropy />} />
            <Route path="/philanthropy/grants" element={<PhilanthropyGrants />} />

            {/* Legal — one page, three tabs, each with its own URL */}
            <Route path="/legal" element={<LegalHub />} />
            <Route path="/terms" element={<LegalHub docId="terms" />} />
            <Route path="/trademark" element={<LegalHub docId="trademark" />} />
            <Route path="/cookies" element={<LegalHub docId="cookies" />} />

            {/* Retired v2 marketing URLs — redirects so existing links and
                bookmarks land somewhere sensible instead of a 404. */}
            <Route path="/privacy" element={<Navigate to="/data-privacy" replace />} />
            <Route path="/about" element={<Navigate to="/leadership" replace />} />
            <Route path="/about/leadership" element={<Navigate to="/leadership" replace />} />
            <Route path="/about/purpose" element={<Navigate to="/leadership" replace />} />
            <Route path="/about/careers" element={<Navigate to="/careers" replace />} />
            <Route path="/help" element={<Navigate to="/resources" replace />} />
            <Route path="/download" element={<Navigate to="/welcome" replace />} />
            <Route path="/hitl-ai" element={<Navigate to="/neurodivergent-explained" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </SubscriptionProvider>
    </Router>
  );
}

export default App;
